// AI Generated File
import { Component, EventEmitter, Input, Output, TemplateRef, ViewChild, OnInit } from '@angular/core';
import { PageFixService } from '@client/app/modules/page-fix/services/page-fix/page-fix.service';
import { PageFixRepoService } from '@client/app/services/api/pagefix-repo/page-fix-repo.service';
import { AccessibilityAutofixService } from '@client/app/modules/page-fix/services/accessibility-auto-fix/accessibility-auto-fix.service';
import { PAGE_FIX_FIXABLE_TYPES } from '@client/app/modules/page-fix/constants/page-fix-fixable-types';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';

@Component({
    selector: 'page-fix-icon',
    standalone: false,
    template: `
        <div class="page-fix-icon" *ngIf="showPageFix">
            <button data-test="page-correct-button"
                    class="btn btn-border-radius btn-secondary"
                    *ngIf="!isFixed()"
                    (click)="add()"
                    translate>
                PageCorrect
            </button>
            <button ngbTooltip="{{ 'PageCorrect' | translate }}"
                    triggers="focus hover"
                    *ngIf="isFixed()"
                    class="btn btn-border-radius btn-link"
                    (click)="openPopup()"
                    [ngbPopover]="popoverTemplate"
                    popoverClass="popover-bs3 monsido-card text-ellipsis"
                    placement="bottom-right"
                    [animation]="false"
                    autoClose="outside"
                    [attr.aria-label]="'Edit or Delete PageCorrect' | translate"
                    (clickOutside)="notesPopupOpen = false"
                    monEventTrigger="actionTrigger"
                    [monEventParams]="{action: 'page-correct-btn', params: {type: type, typeId: typeId}}">
                <span class="fas fa-shield-check fa-2x text-success" aria-hidden="true"></span>
            </button>
        </div>

        <ng-template #popoverTemplate>
            <h3 class="popover-title" translate>PageCorrect Details</h3>
            <div class="popover-content px-0">
                <div class="d-flex align-items-center px-4">
                    <mon-spinner [loading]="!data">
                        <ng-container>
                            <page-fix-changeset-spelling *ngIf="type === fixableTypes.misspelling" [data]="data">
                            </page-fix-changeset-spelling>
                            <page-fix-changeset-link *ngIf="type === fixableTypes.link" [data]="data">
                            </page-fix-changeset-link>
                            <page-fix-changeset-accessibility
                                    *ngIf="type === fixableTypes.accessibility_source_code || type === fixableTypes.accessibility_check"
                                    [data]="data">
                            </page-fix-changeset-accessibility>
                        </ng-container>
                    </mon-spinner>
                </div>

                <hr class="mt-0"/>
                <div class="text-right px-4">
                    <button type="button"
                            (click)="remove()"
                            class="btn btn-border-radius btn-secondary mr-2"
                            translate>Delete</button>
                    <button type="button"
                            (click)="update()"
                            [disabled]="data === null"
                            class="btn btn-border-radius btn-marine-blue"
                            translate>
                        Update
                    </button>
                </div>
            </div>
        </ng-template>
    `,
})
export class PageFixIconComponent implements OnInit {
    @Input() model: any;
    @Input() type!: any;
    @Input() subType?: string;
    @Input() typeId?: string;
    @Output() cb = new EventEmitter<any>();

    @ViewChild('popoverTemplate', { static: true }) popoverTemplate!: TemplateRef<any>;

    showPageFix = true;
    notesPopupOpen = false;
    data: any = null;
    fixableTypes = PAGE_FIX_FIXABLE_TYPES;

    constructor (
        private pageFixService: PageFixService,
        private pageFixRepoService: PageFixRepoService,
        private accessibilityAutofixService: AccessibilityAutofixService,
        private activeFeatureService: ActiveFeatureService,
        private monEventService: MonEventService,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    private activate (): void {
        this.showPageFix = true;
        if (this.type === PAGE_FIX_FIXABLE_TYPES.accessibility_source_code) {
            this.showPageFix = this.activeFeatureService.isFeatureActive('page_fix_accessibility_source_code');
        } else if (this.type === PAGE_FIX_FIXABLE_TYPES.accessibility_check) {
            this.showPageFix = this.activeFeatureService.isFeatureActive('page_fix_accessibility_check');
        }
    }

    openPopup (): void {
        this.fixableTypes = PAGE_FIX_FIXABLE_TYPES;
        this.data = null;
        if (this.notesPopupOpen === undefined || this.notesPopupOpen === false) {
            this.pageFixRepoService.getAll({ fixable_type: this.type, fixable_id: this.typeId || this.model.id } as any)
                .then((pageFixes: any) => {
                    const fixes = pageFixes.data || pageFixes;
                    if (
                        this.type === PAGE_FIX_FIXABLE_TYPES.accessibility_source_code ||
                        this.type === PAGE_FIX_FIXABLE_TYPES.accessibility_check
                    ) {
                        for (let i = 0; i < fixes.length; i++) {
                            if (this.accessibilityAutofixService.isCheckFixed(fixes[i], this.model)) {
                                this.data = fixes[i];
                                break;
                            }
                        }
                    } else {
                        this.data = fixes[0];
                    }
                });
        } else {
            this.data = null;
        }
    }

    add (): void {
        this.pageFixService.add(
            this.type,
            this.model,
            this.typeId,
            this.subType,
            (result: any) => {
                if (this.type === PAGE_FIX_FIXABLE_TYPES.link && this.model.link) {
                    this.model.link.page_fixed = result !== undefined;
                } else {
                    this.model.page_fixed = result !== undefined;
                }
                if (this.cb) {
                    this.cb.emit(this.model);
                }
            },
        );
    }

    remove (): void {
        this.pageFixService.remove(this.data).then((result: any) => {
            if (this.type === PAGE_FIX_FIXABLE_TYPES.link && this.model.link) {
                this.model.link.page_fixed = result !== undefined;
            } else {
                this.model.page_fixed = result === undefined;
            }
        });
    }

    isFixed (): boolean {
        if (this.type === PAGE_FIX_FIXABLE_TYPES.link && this.model.link) {
            return this.model.link.page_fixed;
        } else {
            return this.model.page_fixed;
        }
    }

    update (): void {
        this.pageFixService.update(this.type, this.model, this.subType, this.data);
    }
}
